import React, { useCallback, useEffect, useRef, useState } from 'react';
import shippingDocumentService from '../services/shippingDocumentService';
import useShippingRequest from '../hooks/useShippingRequest';
import { saveShippingDocument } from '../utils/shippingDocumentDownload';
import { shippingDownloadError } from './ShippingDocuments';
import { useT } from '../i18n/LanguageContext';

export default function ShippingDocumentReview() {
  const t = useT();
  const [rows, setRows] = useState([]);
  const [state, setState] = useState('quarantined');
  const [before, setBefore] = useState(undefined);
  const [nextBefore, setNextBefore] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [orderNumber, setOrderNumber] = useState('');
  const [targets, setTargets] = useState(null);
  const [target, setTarget] = useState(null);
  const [downloaded, setDownloaded] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [stale, setStale] = useState(false);
  const { begin, version } = useShippingRequest(`document-review:${state}:${before || ''}`);
  const load = useCallback(async () => {
    const request = begin();
    lock.current = false; setBusy(false); setLoading(true); setError(''); setRows([]); setSelected(null); setStale(false);
    try {
      const data = await shippingDocumentService.reviewQueue({ before, state }, request);
      if (request.isCurrent()) { setRows(data.documents); setNextBefore(data.nextBefore); }
    } catch { if (request.isCurrent()) setError('Could not load document reviews. Please try again.'); }
    finally { if (request.isCurrent()) setLoading(false); }
  }, [before, begin, state]);
  useEffect(() => { load(); }, [load, version]);
  const run = async (action, errorMessage) => {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError('');
    const request = begin();
    try { await action(request); }
    catch (failure) {
      if (request.isCurrent()) {
        setError(typeof errorMessage === 'function' ? errorMessage(failure) : errorMessage);
        setConfirmed(false);
      }
    } finally { if (request.isCurrent()) { lock.current = false; setBusy(false); } }
  };
  const choose = row => {
    setSelected(row); setOrderNumber(row.orderNumber || ''); setTargets(null); setTarget(null);
    setDownloaded(false); setConfirmed(false); setError(''); setStale(false);
  };
  const download = () => run(async request => {
    setDownloaded(false); setConfirmed(false);
    const response = await shippingDocumentService.downloadForReview(selected, request);
    if (request.isCurrent()) {
      const saved = await saveShippingDocument(response, { isCurrent: request.isCurrent });
      if (request.isCurrent()) setDownloaded(saved === true);
    }
  }, shippingDownloadError);
  const findTargets = () => run(async request => {
    setTargets(null); setTarget(null); setConfirmed(false);
    const result = await shippingDocumentService.targets(orderNumber, request);
    if (request.isCurrent()) setTargets(result);
  }, 'Could not find shipments for that order. Check the order number and try again.');
  const decide = decision => run(async request => {
    if (stale || !selected || (decision === 'approve' && (!downloaded || !confirmed || !target))) return;
    // An uncertain write must be reconciled by reloading, never retried against an old decision.
    setStale(true);
    await shippingDocumentService.decide({ ...selected, ...(decision === 'approve' ? target : {}) }, decision, request);
    if (request.isCurrent()) {
      setRows(current => current.filter(row => row._id !== selected._id));
      setSelected(null); setConfirmed(false); setStale(false);
    }
  }, failure => failure?.response?.status === 409 ? 'This shipping document changed. Refresh and review it again.' :
    'Could not confirm the document review. Refresh before trying again.');
  return <div>
    <p className="text-sm text-slate-600 mb-3">{t('Review every page before sharing a shipping slip. Documents stay private until approved for one order and shipment.')}</p>
    <div role="tablist" aria-label={t('Document status')} className="mb-3 flex flex-wrap gap-2">
      {[['quarantined', 'Needs review'], ['ready', 'Approved'], ['rejected', 'Rejected']].map(([value, label]) =>
        <button key={value} role="tab" type="button" aria-selected={state === value} disabled={busy} onClick={() => { setState(value); setBefore(undefined); }}
          className={`rounded-lg px-3 py-2 text-sm ${state === value ? 'bg-blue-50 text-[#0089ff]' : 'text-slate-600'}`}>{t(label)}</button>)}
    </div>
    {state === 'ready' && <p className="mb-3 text-sm text-slate-600">{t('Approved documents may need another review after shipping records change.')}</p>}
    <button type="button" disabled={loading || busy} onClick={load} className="text-sm text-[#0089ff] mb-3">{t('Refresh reviews')}</button>
    {error && <p role="alert" className="text-sm text-red-700 mb-3">{t(error)}</p>}
    {loading ? <p role="status">{t('Loading document reviews...')}</p> : !selected ? <>
      {!rows.length && !error && <p className="text-sm text-slate-500">{t(state === 'quarantined' ? 'No shipping documents need review.' : 'No shipping documents found.')}</p>}
      <ul className="space-y-2">{rows.map((row, index) => <li key={row._id} className="flex items-center justify-between gap-3 rounded-lg border p-3">
        <span className="text-sm">{t('Shipping slip {n}', { n: index + 1 })} ({Math.ceil(row.byteLength / 1024)} KB)</span>
        <button type="button" disabled={busy} onClick={() => choose(row)} className="text-sm text-[#0089ff]">{t('Review document')}</button>
      </li>)}</ul>
      <div className="mt-3 flex justify-end gap-3">
        {before && <button type="button" disabled={busy} onClick={() => setBefore(undefined)}>{t('First page')}</button>}
        {nextBefore && <button type="button" disabled={busy} onClick={() => setBefore(nextBefore)}>{t('Next')}</button>}
      </div>
    </> : <div className="space-y-4 rounded-lg border p-4">
      <button type="button" disabled={busy || stale} onClick={() => setSelected(null)} className="text-sm text-[#0089ff]">{t('Back to reviews')}</button>
      <div><button type="button" disabled={busy || stale} onClick={download} className="rounded-lg bg-[#0089ff] px-3 py-2 text-sm text-white disabled:opacity-50">{t('Download PDF for review')}</button></div>
      {selected.state === 'rejected' ? <p className="text-sm text-slate-600">{t('Rejected documents cannot be shared.')}</p> : <>
      <label className="block text-sm">{t('Order number')}
        <input value={orderNumber} maxLength={100} disabled={busy || stale} onChange={event => { setOrderNumber(event.target.value); setTargets(null); setTarget(null); setConfirmed(false); }}
          className="mt-1 block w-full rounded-lg border px-3 py-2" />
      </label>
      <button type="button" disabled={busy || stale || !orderNumber.trim()} onClick={findTargets} className="text-sm text-[#0089ff] disabled:opacity-50">{t('Find shipments')}</button>
      {targets && !targets.length && <p className="text-sm text-slate-600">{t('No eligible shipments match that order number.')}</p>}
      {!!targets?.length && <label className="block text-sm">{t('Shipment for this document')}
        <select disabled={busy || stale} value={target?.shipmentId || ''} onChange={event => { setTarget(targets.find(row => row.shipmentId === event.target.value) || null); setConfirmed(false); }}
          className="mt-1 block w-full rounded-lg border p-2">
          <option value="">{t('Choose a shipment...')}</option>
          {targets.map(row => <option key={row.shipmentId} value={row.shipmentId}>{row.orderNumber} — {row.trackingNumber}</option>)}
        </select>
      </label>}
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" checked={confirmed} disabled={busy || stale || !downloaded || !target} onChange={event => setConfirmed(event.target.checked)} className="mt-1" />
        {t('I reviewed every page. This PDF contains only the selected order and belongs to this shipment.')}
      </label>
      <div className="flex flex-wrap gap-3">
        <button type="button" disabled={busy || stale || !downloaded || !target || !confirmed} onClick={() => decide('approve')}
          className="rounded-lg bg-[#0089ff] px-3 py-2 text-sm text-white disabled:opacity-50">{t('Approve document')}</button>
        <button type="button" disabled={busy || stale} onClick={() => decide('reject')} className="rounded-lg border px-3 py-2 text-sm text-red-700 disabled:opacity-50">{t('Reject document')}</button>
      </div>
      </>}
    </div>}
    {busy && <p role="status" className="mt-3 text-sm text-slate-500">{t('Updating document review...')}</p>}
  </div>;
}
